import React, { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import { 
  Plus, 
  Phone, 
  MessageCircle, 
  Video, 
  Calendar, 
  AlertCircle,
  Heart,
  X,
  ChevronUp,
  Headphones,
  FileText,
  Users,
  Settings
} from 'lucide-react';

interface FloatingAction {
  icon: React.ComponentType<any>;
  label: string;
  action: () => void;
  color: 'primary' | 'emergency' | 'success' | 'warning';
  shortcut?: string;
}

export const FloatingActionButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const floatingActions: FloatingAction[] = [
    {
      icon: Phone,
      label: 'Emergency Call',
      action: () => console.log('Emergency call initiated'),
      color: 'emergency',
      shortcut: 'E'
    },
    {
      icon: Video,
      label: 'Start Video Session',
      action: () => console.log('Video session started'),
      color: 'primary',
      shortcut: 'V'
    },
    {
      icon: MessageCircle,
      label: 'Quick Message',
      action: () => console.log('Message opened'),
      color: 'primary',
      shortcut: 'M'
    },
    {
      icon: Calendar,
      label: 'Schedule Appointment',
      action: () => console.log('Scheduling opened'),
      color: 'success',
      shortcut: 'S'
    },
    {
      icon: FileText,
      label: 'Quick Notes',
      action: () => console.log('Notes opened'),
      color: 'primary',
      shortcut: 'N'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        const action = floatingActions.find(a => 
          a.shortcut && e.key.toLowerCase() === a.shortcut.toLowerCase()
        );
        if (action) {
          e.preventDefault();
          action.action();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [floatingActions]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getButtonColor = (color: string) => {
    switch (color) {
      case 'emergency':
        return 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/25';
      case 'success':
        return 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/25';
      case 'warning':
        return 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/25';
      default:
        return 'bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white shadow-lg shadow-clinic-blue-600/25';
    }
  };

  return (
    <>
      {/* Main Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Action Menu */}
        <div className={`mb-4 space-y-3 transition-all duration-300 ${
          isOpen ? 'opacity-100 visible transform translate-y-0' : 'opacity-0 invisible transform translate-y-4'
        }`}>
          {floatingActions.map((action, index) => (
            <div
              key={index}
              className="flex items-center space-x-3 animate-slide-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Tooltip */}
              <div className="bg-clinic-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                {action.label}
                {action.shortcut && (
                  <span className="ml-2 text-xs bg-white/20 px-1.5 py-0.5 rounded">
                    ⌘{action.shortcut}
                  </span>
                )}
              </div>
              
              {/* Action Button */}
              <Button
                onClick={action.action}
                className={`group w-14 h-14 rounded-full p-0 ${getButtonColor(action.color)} hover:shadow-xl transition-all duration-200 hover:scale-110`}
              >
                <action.icon className="w-6 h-6" />
              </Button>
            </div>
          ))}
        </div>

        {/* Primary FAB */}
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-16 h-16 rounded-full p-0 bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white shadow-2xl transition-all duration-300 ${
            isOpen ? 'rotate-45 scale-110' : 'rotate-0 scale-100'
          } hover:scale-110 animate-glow`}
          style={{
            boxShadow: 'var(--shadow-2xl)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = 'var(--shadow-3xl)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'var(--shadow-2xl)';
          }}
        >
          {isOpen ? (
            <X className="w-7 h-7" />
          ) : (
            <Plus className="w-7 h-7" />
          )}
        </Button>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <div className="fixed bottom-6 left-6 z-50">
          <Button
            onClick={scrollToTop}
            className="w-12 h-12 rounded-full p-0 bg-clinic-gray-700 hover:bg-clinic-gray-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 animate-fade-in"
          >
            <ChevronUp className="w-5 h-5" />
          </Button>
        </div>
      )}

      {/* Emergency Quick Access - Always Visible */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          onClick={() => console.log('Emergency activated')}
          className="btn-emergency px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:shadow-xl animate-pulse-slow"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          Emergency
        </Button>
      </div>

      {/* Health Monitor Widget */}
      <div className="fixed top-4 left-4 z-40">
        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/20 card-glass">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
              <Heart className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">System Status</p>
              <p className="text-xs text-green-600">All Services Online</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Sidebar */}
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-40 space-y-3">
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-white/20 card-glass text-center">
          <Users className="w-6 h-6 text-clinic-blue-600 mx-auto mb-1" />
          <p className="text-xs font-medium text-gray-900">42</p>
          <p className="text-xs text-gray-600">Online</p>
        </div>
        
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-white/20 card-glass text-center">
          <Calendar className="w-6 h-6 text-green-600 mx-auto mb-1" />
          <p className="text-xs font-medium text-gray-900">18</p>
          <p className="text-xs text-gray-600">Today</p>
        </div>
        
        <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-white/20 card-glass text-center">
          <MessageCircle className="w-6 h-6 text-amber-600 mx-auto mb-1" />
          <p className="text-xs font-medium text-gray-900">7</p>
          <p className="text-xs text-gray-600">Messages</p>
        </div>
      </div>

      {/* Global Shortcuts Help */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-30 opacity-0 hover:opacity-100 transition-opacity duration-300">
        <div className="bg-clinic-gray-900 text-white px-4 py-2 rounded-full text-xs">
          Press ⌘ + E for Emergency • ⌘ + V for Video • ⌘ + M for Messages
        </div>
      </div>
    </>
  );
};